/* eslint-disable eag/match-named-export, eag/no-index -- Pre-existing violation */
import {BaseFlagProps} from 'platform/components';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {CashReceiptV4ResponseBodyRead, InvoicingDocumentItemResponseBody} from '@dms/api/core';
import {
  useGetCashRegisterDocumentKindOfPaymentListQuery,
  useGetCashRegisterQuery,
} from '@dms/api/finance';
import {
  CashRegisterDocumentItemResponseBody,
  CashRegisterDocumentKindOfPaymentEnum,
  CashRegisterDocumentStateEnum,
  IssuerResponseBody,
  SupplierResponseBody,
} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {getApiDateString, getRandomId, Nullish, parseDate} from 'shared/web';

import {AccountingDocumentFormValues} from '../../../types';

export const getCashReceiptStateFlagProps = (
  state: CashRegisterDocumentStateEnum
): BaseFlagProps => {
  const variableProps = match<CashRegisterDocumentStateEnum, BaseFlagProps>(state)
    .with('taken', always({label: i18n.t('entity.cashRegister.state.taken'), colorScheme: 'green'}))
    .with(
      'cancelled',
      always({label: i18n.t('entity.cashRegister.state.cancelled'), colorScheme: 'red'})
    )
    .with(
      'cancelling',
      always({label: i18n.t('entity.cashRegister.state.cancelling'), colorScheme: 'orange'})
    )
    .exhaustive();

  return {...variableProps, isSubtle: true};
};

export const covertIssuerToSupplier = (
  data: IssuerResponseBody | Nullish
): SupplierResponseBody => ({
  cin: data?.registrationNumber ?? null,
  tin: data?.vatNumber ?? null,
  id: getRandomId(),
  country: data?.country ?? null,
  city: data?.city ?? null,
  name: data?.name ?? null,
  tradeName: null,
  addressLine1: data?.street ?? null,
  zip: data?.zip ?? null,
  addressLine2: null,
  fileNumber: null,
});

export const convertCashRegisterItemToInvoiceItem = (
  item: CashRegisterDocumentItemResponseBody
): InvoicingDocumentItemResponseBody => ({
  isUnitPriceWithVat: item.withVat,
  description: item.description,
  currency: item.totalPriceWithVat.currency,
  discount: null,
  generatedItemType: 'standard',
  vatType: item.vatType,
  pricePerUnit: item.unitPrice,
  quantity: item.quantity,
  itemId: item.id,
  relatedItemId: null,
  totalPrice: {
    vatType: item.vatType,
    vatRate: item.vatRate,
    total: item.totalPriceWithVat,
    base: item.totalPriceWithoutVat,
    vat: {amount: '0', currency: 'CZK'},
  },
  unit: item.unit,
  type: 'standard',
});

export const useGetDefaultValuesFromCashReceipt = (
  cashReceipt: CashReceiptV4ResponseBodyRead | Nullish
): Partial<AccountingDocumentFormValues> | undefined => {
  const {data: kindOfPayment} = useGetCashRegisterDocumentKindOfPaymentListQuery({});
  const {data: cashRegister} = useGetCashRegisterQuery(
    {cashRegisterId: cashReceipt?.cashRegisterId ?? ''},
    {skip: isNil(cashReceipt)}
  );

  if (isNil(cashReceipt) || isNil(cashRegister)) {
    return undefined;
  }

  return {
    cashRegisterId: cashRegister.name,
    issueDate: getApiDateString(parseDate(cashReceipt.issuedAt)),
    paymentInfo: {
      paymentMethod:
        cashReceipt.paymentMethod === 'CARD'
          ? i18n.t('entity.invoice.paymentMethod.card')
          : i18n.t('entity.invoice.paymentMethod.cash'),
      variableSymbol: cashReceipt.variableSymbol,
    },
    paymentType:
      (kindOfPayment?.find((item) => item.code === cashReceipt.kindOfPayment)
        ?.name as CashRegisterDocumentKindOfPaymentEnum) ?? null,
    reason: cashReceipt.purpose,
    footerNote: cashReceipt.note,
    exchangeRateRatio: {
      amount: cashReceipt.exchangeRateRatio?.amount?.toString() ?? null,
      ratio: cashReceipt.exchangeRateRatio?.ratio ?? null,
      currency: null,
      type: 'recalculate',
    },
  };
};
